'use client';

import { MENU_ITEMS, MenuItem } from "@/app/features/sites/header/types/menu-items";
import { Box, ListItemButton, ListItemText, Stack, Menu, MenuItem as MuiMenuItem } from "@mui/material";
import Centerpiece from "@/app/features/sites/header/ui/desktop/Centerpiece";
import Grid from "@mui/material/Grid2";
import { signatureBorderStyle } from "@/app/theme/theme-tokens";
import { useState, useRef } from "react";
import Link from "next/link";
import { ArrowDropDown } from "@mui/icons-material";

interface MainMenuItemProps {
    item: MenuItem;
    index: number;
    focusedIndex: number;
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>, index: number) => void;
    registerRef: (el: HTMLElement | null, index: number) => void;
}

function MainMenuItem({ item, index, focusedIndex, onKeyDown, registerRef }: MainMenuItemProps) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (item.children && item.children.length > 0) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleKeyDownInternal = (event: React.KeyboardEvent<HTMLElement>) => {
        if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && item.children && item.children.length > 0) {
            event.preventDefault();
            setAnchorEl(event.currentTarget);
            // MUI Menu will handle focus within the menu
        } else {
            onKeyDown(event, index);
        }
    };

    const buttonProps = item.route ? {
        component: Link,
        href: item.route,
        prefetch: true
    } : {};

    return (
        <>
            <ListItemButton
                {...buttonProps}
                id={`menu-item-${index}`}
                ref={(el) => registerRef(el, index)}
                role="menuitem"
                aria-haspopup={item.children ? "true" : undefined}
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
                onKeyDown={handleKeyDownInternal}
                tabIndex={focusedIndex === index ? 0 : -1}
                sx={{
                    pr: item.children ? 1 : 2,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    ...signatureBorderStyle,
                }}
            >
                <ListItemText
                    primary={item.label}
                    slotProps={{
                        primary: {
                            sx: {
                                fontSize: 14,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                color: open ? 'primary.main' : 'inherit',
                                transition: 'color 0.2s',
                            }
                        }
                    }}
                />
                {item.children && (
                    <ArrowDropDown 
                        className="ml-1" 
                        sx={{ 
                            transition: 'transform 0.2s, color 0.2s',
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            color: open ? 'primary.main' : 'inherit',
                        }}
                    />
                )}
            </ListItemButton>
            {item.children && item.children.length > 0 && (
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                        'role': 'menu',
                        'aria-labelledby': `menu-item-${index}`,
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                p: 1,
                                mt: 1,
                                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                                border: '1px solid rgba(218, 156, 35, 0.2)',
                                '& .MuiList-root': {
                                    padding: 0,
                                },
                            },
                        }
                    }}
                >
                    {item.children.map((child) => (
                        <MuiMenuItem 
                            key={child.id} 
                            onClick={handleClose}
                            component={child.route ? Link : 'li'}
                            {...(child.route ? { href: child.route, prefetch: true } : {})}
                            role="menuitem"
                            sx={{
                                ...signatureBorderStyle,
                                py: 1.5,
                                px: 2,
                                fontSize: '0.875rem',
                                '&:hover': {
                                    backgroundColor: 'rgba(218, 156, 35, 0.08)',
                                },
                            }}
                        >
                            {child.label}
                        </MuiMenuItem>
                    ))}
                </Menu>
            )}
        </>
    );
}

export default function MainMenu() {
    const leftItems = MENU_ITEMS.filter(item => item.side === 'left');
    const rightItems = MENU_ITEMS.filter(item => item.side === 'right');
    const allItems = [...leftItems, ...rightItems];
    
    const [focusedIndex, setFocusedIndex] = useState(0);
    const itemRefs = useRef<(HTMLElement | null)[]>([]);

    const registerRef = (el: HTMLElement | null, index: number) => {
        itemRefs.current[index] = el;
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>, index: number) => {
        let newIndex = -1;

        switch (event.key) {
            case 'ArrowRight':
                newIndex = (index + 1) % allItems.length;
                break;
            case 'ArrowLeft':
                newIndex = (index - 1 + allItems.length) % allItems.length;
                break;
            case 'Home':
                newIndex = 0;
                break;
            case 'End':
                newIndex = allItems.length - 1;
                break;
        }

        if (newIndex !== -1) {
            event.preventDefault();
            setFocusedIndex(newIndex);
            itemRefs.current[newIndex]?.focus();
        }
    };

    return (
        <Box
            className="w-full p-2 justify-items-center"
            sx={{
                borderBottom: '1px solid rgba(218, 156,35, 0.125)',
            }}
        >
            <Grid
                container
                role="menubar"
                component="nav"
                className="self-center max-w-[var(--content-max-width)]"
            >
                <Grid size={5} role="none" className="content-center overflow-auto justify-items-end">
                    <Stack direction="row" role="none" className="px-2" spacing={2}>
                        {leftItems.map((item, index) => (
                            <MainMenuItem 
                                key={`${item.id}-${index}`} 
                                item={item} 
                                index={index}
                                focusedIndex={focusedIndex}
                                onKeyDown={handleKeyDown}
                                registerRef={registerRef}
                            />
                        ))}
                    </Stack>
                </Grid>
                <Grid size={2} role="none">
                    <Centerpiece />
                </Grid>
                <Grid size={5} role="none" className="content-center overflow-auto justify-items-start">
                    <Stack direction="row" role="none" className="px-2" spacing={2}>
                        {rightItems.map((item, index) => (
                            <MainMenuItem 
                                key={`${item.id}-${index}`} 
                                item={item} 
                                index={index + leftItems.length}
                                focusedIndex={focusedIndex}
                                onKeyDown={handleKeyDown}
                                registerRef={registerRef}
                            />
                        ))}
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
}
